import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home, Loader2, Building2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: sp.get("role") === "landlord" ? "landlord" : "renter" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created!");
      navigate(next || "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const RoleBtn = ({ value, icon: Icon, label, desc }) => (
    <button
      type="button"
      data-testid={`role-${value}`}
      onClick={() => setForm({ ...form, role: value })}
      className={`flex-1 text-left border rounded-xl p-4 transition-all ${
        form.role === value ? "border-forest bg-forest/5 ring-2 ring-forest/20" : "border-line hover:border-forest/40"
      }`}
    >
      <Icon className={`h-5 w-5 ${form.role === value ? "text-forest" : "text-mute"}`} strokeWidth={1.75} />
      <p className="mt-2 font-medium text-ink text-sm">{label}</p>
      <p className="text-xs text-mute">{desc}</p>
    </button>
  );

  return (
    <div className="min-h-screen bg-bone flex flex-col items-center justify-center px-5 py-10">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <div className="h-9 w-9 rounded-lg bg-forest flex items-center justify-center">
          <Home className="h-5 w-5 text-white" />
        </div>
        <span className="font-heading font-semibold text-lg text-ink">LocalLease</span>
      </Link>

      <div className="w-full max-w-md bg-white border border-line rounded-2xl shadow-sm p-8">
        <h1 className="text-2xl font-heading font-semibold text-ink">Create your account</h1>
        <p className="text-sm text-mute mt-1">Choose how you'll use LocalLease.</p>

        <div className="mt-5 flex gap-3">
          <RoleBtn value="renter" icon={KeyRound} label="I'm a Renter" desc="Find & rent a home" />
          <RoleBtn value="landlord" icon={Building2} label="I'm a Landlord" desc="List & manage" />
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <Label>Full name</Label>
            <Input data-testid="register-name" required value={form.name} onChange={set("name")} className="mt-1.5 bg-white border-line" placeholder="Jamie Doe" />
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 bg-white border-line" placeholder="you@example.com" />
          </div>
          <div>
            <Label>Phone (for text reminders)</Label>
            <Input data-testid="register-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 bg-white border-line" placeholder="+1 503 555 0100" />
          </div>
          <div>
            <Label>Password</Label>
            <Input data-testid="register-password" type="password" required value={form.password} onChange={set("password")} className="mt-1.5 bg-white border-line" placeholder="••••••••" />
          </div>
          {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
          <button data-testid="register-submit" disabled={loading} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
          </button>
        </form>

        <p className="text-sm text-mute text-center mt-6">
          Already have an account? <Link to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-forest font-medium hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}
